import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useData } from '../contexts/DataContext'
import { useLocation } from '../contexts/LocationContext'
import { useFilters } from '../contexts/FilterContext'
import { useView } from '../contexts/ViewContext'
import LocationSelector from '../components/selectors/LocationSelector'
import CategorySelector from '../components/selectors/CategorySelector'
import ViewSelector from '../components/selectors/ViewSelector'
import FilterPanel from '../components/filters/FilterPanel'
import MapView from './MapView'
import GridView from './GridView'
import ChartsView from './ChartsView'
import ComparisonView from './ComparisonView'
import TrendsView from './TrendsView'
import { useFilteredFacilities } from '../hooks/useFilteredFacilities'

function Dashboard() {
  const { view } = useParams()
  const {
    config,
    locations: _locations,
    attributes: _attributes,
    loading,
    loadFacilityData,
    loadAttributesData,
  } = useData()
  const { selectedDistrict, setDistrict } = useLocation()
  const { activeCategory } = useFilters()
  const { activeView, setActiveView } = useView()
  const { facilities: _facilities, total, filtered } = useFilteredFacilities()

  // Set active view from URL
  useEffect(() => {
    if (view && view !== activeView) {
      setActiveView(view)
    }
  }, [view, activeView, setActiveView])

  // Set default district
  useEffect(() => {
    if (!selectedDistrict && config?.app?.default_location) {
      setDistrict(config.app.default_location) // Default to Kayunga for now
    }
  }, [selectedDistrict, config, setDistrict])

  // Load facility data and attributes when category changes
  useEffect(() => {
    loadFacilityData(activeCategory)
    loadAttributesData(activeCategory)
  }, [activeCategory, loadFacilityData, loadAttributesData])

  if (loading.config || loading.locations) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Global Selectors */}
      <div className="panel p-4 bg-gradient-to-r from-white to-surface-50">
        <div className="space-y-4">
          <LocationSelector />

          {/* Category and View Selector */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-surface-100">
            <CategorySelector />
            <ViewSelector />
          </div>
        </div>
      </div>

      {/* Main Content with Filter Panel */}
      <div
        className={`grid grid-cols-1 gap-4 ${activeView === 'trends' ? 'lg:grid-cols-1' : 'lg:grid-cols-4'}`}
      >
        {activeView !== 'trends' && (
          <div className="lg:col-span-1">
            <FilterPanel />
          </div>
        )}

        {/* Main Content Area */}
        <div
          className={activeView === 'trends' ? 'col-span-1' : 'lg:col-span-3'}
        >
          <div className="chart-container min-h-96">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-surface-900 flex items-center">
                <span className="text-2xl mr-3">
                  {activeView === 'map'
                    ? '🗺️'
                    : activeView === 'grid'
                      ? '📋'
                      : activeView === 'charts'
                        ? '📊'
                        : activeView === 'comparison'
                          ? '📈'
                          : activeView === 'trends'
                            ? '📈'
                            : '🔍'}
                </span>
                {activeView.charAt(0).toUpperCase() + activeView.slice(1)} View
              </h3>
              {activeView !== 'trends' && (
                <div className="text-sm text-surface-600 bg-surface-100 px-3 py-2 rounded-lg font-medium">
                  {filtered !== total ? (
                    <span>
                      Showing{' '}
                      <span className="font-semibold text-primary-600">
                        {filtered}
                      </span>{' '}
                      of <span className="font-semibold">{total}</span> items
                    </span>
                  ) : (
                    <span>
                      <span className="font-semibold text-primary-600">
                        {total}
                      </span>{' '}
                      items
                    </span>
                  )}
                </div>
              )}
            </div>

            {(() => {
              if (loading[`facilities_${activeCategory}`]) {
                return (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading data...</p>
                    </div>
                  </div>
                )
              }

              switch (activeView) {
                case 'map':
                  return <MapView />
                case 'grid':
                  return <GridView />
                case 'charts':
                  return <ChartsView />
                case 'comparison':
                  return <ComparisonView />
                case 'trends':
                  return <TrendsView />
                default:
                  return (
                    <div className="flex items-center justify-center h-64 text-gray-500">
                      <div className="text-center">
                        <div className="text-4xl mb-2">🚧</div>
                        <p>View not found</p>
                      </div>
                    </div>
                  )
              }
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
